import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as xml2js from "xml2js";
import { ToastrService } from "ngx-toastr";
import { FileNode } from "../rule-config-component/models/rule-engine";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class DataParserService {
  constructor(
    private toastr: ToastrService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private httpClient: HttpClient
  ) {}

  private dataParserSubject = new BehaviorSubject<any>(null);
  private nodePathsSubject = new BehaviorSubject<any>(null);
  private nodesWithLeavesSubject = new BehaviorSubject<any>(null);
  private matTreeJsonSubject = new BehaviorSubject<any>(null);

  //private httpClient:HttpClient;
  public jsonData: any;
  public copyOfJsonData: any;
  uniqueId: number = 0;
  jsonForMatTree: any;
  copyOfJsonForMatTree: any;
  selectedButton: string = "";
  private xmlData: any;
  public APNodePaths: any;
  public CPNodePaths: any;

  async parseXmlToJson(data: any) {
    let jsonData = await this.convertXmlToJson(data);
    this.setJsonDataForMatTree(jsonData);
  }

  setJsonDataForMatTree(jsonData: any) {
    this.dataParserSubject.next(jsonData);
  }

  $getJsonDataForMatTree() {
    return this.dataParserSubject.asObservable();
  }

  setNodePaths(nodePaths: any) {
    this.nodePathsSubject.next(nodePaths);
  }

  $getNodePaths = this.nodePathsSubject.asObservable();

  setMatTreeJson(jsonForMatTree: any) {
    this.matTreeJsonSubject.next(jsonForMatTree);
  }
  $getMatTreeJson = this.matTreeJsonSubject.asObservable();

  private async convertXmlToJson(xmlData: any): Promise<any> {
    const parser = new xml2js.Parser({
      strict: true,
      trim: true,
      explicitArray: false,
      mergeAttrs: true,
      explicitRoot: true,
      preserveChildrenOrder: true,
    });
    let convertedJsonResult = await parser.parseStringPromise(xmlData);
    //console.log(convertedJsonResult);
    return convertedJsonResult;
  }
  setNodesWithTheirLeafNodes(nodesWithLeaves: any) {
    this.nodesWithLeavesSubject.next(nodesWithLeaves);
  }
  $getNodesWithTheirLeafNodes = this.nodesWithLeavesSubject.asObservable();

  getJsonDataForMatTree(): any {
    this.$getJsonDataForMatTree().subscribe({
      next: (jsonData) => {
        if (jsonData !== null) {
          this.jsonData = jsonData;
          this.copyOfJsonData = structuredClone(this.jsonData);
          this.enableTree();
        }
      },
      error: (err) => {
        this.toastr.error("Unable to generate mat tree", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });
  }

  private enableTree() {
    if (this.jsonData) {
      this.jsonForMatTree = this.buildFileTreeForMatTreeUsingRawJson(
        this.jsonData
      );
      this.copyOfJsonForMatTree = structuredClone(this.jsonForMatTree);
      this.setMatTreeJson(this.jsonForMatTree);
      const allPaths = this.collectAllPaths(this.jsonForMatTree);
      const uniquePaths = [...new Set(allPaths)];
      this.setNodePaths(uniquePaths);
      this.setNodesWithTheirLeafNodes(
        this.collectNodesWithLeaves(this.jsonForMatTree)
      );
    }
  }
  private buildFileTreeForMatTreeUsingRawJson(jsonData: any): FileNode[] {
    const result: FileNode[] = [];

    Object.keys(jsonData).forEach((key) => {
      const value = jsonData[key];

      if (value === null || value === undefined) {
        // If value is null/undefined, create a node with a null leaf
        const node: FileNode = {
          key: key,
          uniqueId: this.uniqueId++,
          leaf: null,
        };
        result.push(node);
      } else if (Array.isArray(value)) {
        // If the value is an array...
        if (value.length > 0 && typeof value[0] === "object") {
          // Array of objects: flatten it.
          // For each item in the array, create a node with the same key.
          value.forEach((item) => {
            const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
            const children = this.buildFileTreeForMatTreeUsingRawJson(item);
            if (children.length > 0) {
              node.children = children;
            }
            result.push(node);
          });
        } else {
          // Array of primitives: create one node with children for each element.
          const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
          node.children = value.map((item) => ({
            key: key,
            uniqueId: this.uniqueId++,
            leaf: item,
          }));
          result.push(node);
        }
      } else if (typeof value === "object") {
        // If the value is an object (but not an array), recursively process it.
        const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
        const children = this.buildFileTreeForMatTreeUsingRawJson(value);
        if (children.length > 0) {
          node.children = children;
        }
        result.push(node);
      } else {
        // If the value is a primitive, just create a leaf node.
        const node: FileNode = {
          key: key,
          uniqueId: this.uniqueId++,
          leaf: value,
        };
        result.push(node);
      }
    });

    return result;
  }

  collectAllPaths(jsonForMatTree: any, path = [], paths: string[] = []): any {
    for (const item of jsonForMatTree) {
      // Add the current item's name to the path
      const currentPath: any = [...path, item.key];

      // If the current item's name matches the target name, return the path
      // if (item.key === targetName && item.uniqueId == uniqueId) {
      //   return currentPath;
      // }
      if (!item.children) {
        const modifiedPath = [...path, `@${item.key}`].join("/");
        paths.push(modifiedPath);
      }

      // If the current item has children, search within the children
      if (item.children) {
        paths.push(...this.collectAllPaths(item.children, currentPath));
      }
    }
    return paths;
  }
  collectNodesWithLeaves(
    tree: FileNode[]
  ): { nodeName: string; leafNodes: string[] }[] {
    const nodesWithLeaves: { nodeName: string; leafNodes: string[] }[] = [];

    // Recursive function to traverse the tree
    const traverse = (treeNodes: FileNode[], currentPath: string[] = []) => {
      for (const node of treeNodes) {
        const currentPathWithNode = [...currentPath, node.key];

        if (node.children) {
          // Recursively process child nodes
          traverse(node.children, currentPathWithNode);
        } else {
          // Leaf node found
          const parentNodeName = currentPath.join("/");
          const leafNode = `@${node.key}`;

          // Add leaf to its parent's entry or create a new one
          const parentNode = nodesWithLeaves.find(
            (n) => n.nodeName === parentNodeName
          );
          if (parentNode) {
            parentNode.leafNodes.push(leafNode);
          } else {
            nodesWithLeaves.push({
              nodeName: parentNodeName,
              leafNodes: [leafNode],
            });
          }
        }
      }
    };

    traverse(tree);

    return nodesWithLeaves;
  }

  public loadCPData() {
    this.selectedButton = "CP";
    const filePath = "/assets/rule-config/CP.xml"; // Update this with your file path //src\assets\rule-config\AP.xml
    //this.fileName = "CP";

    this.ngxUiLoaderService.start();
    this.httpClient
      .get(filePath, { responseType: "text" }) // or 'json' for JSON files
      .subscribe({
        next: (data) => {
          this.ngxUiLoaderService.stop();
          this.xmlData = data;
          this.parseXmlToJson(this.xmlData);
          this.getJsonDataForMatTree();

          //we are now loading the nodepaths of CP to the CPData variable
          this.$getNodePaths.subscribe({
            next: (data: any) => {
              this.CPNodePaths = data;
            },
          });
          this.ngxUiLoaderService.stop();
        },
        error: (err) => {
          this.ngxUiLoaderService.stop();
          this.toastr.error("Unable to load CP data", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }
  public loadAPData() {
    this.selectedButton = "AP";
    const filePath = "/assets/rule-config/AP.xml"; // Update this with your file path
    //this.fileName = "AP";
    this.ngxUiLoaderService.start();
    //this.fetchData();
    this.httpClient
      .get(filePath, { responseType: "text" }) // or 'json' for JSON files
      .subscribe({
        next: (data) => {
          this.xmlData = data;
          this.parseXmlToJson(this.xmlData);
          this.getJsonDataForMatTree();

          //we are now loading the nodepaths of AP to the APData variable
          this.$getNodePaths.subscribe({
            next: (data: any) => {
              this.APNodePaths = data;
            },
          });
          this.ngxUiLoaderService.stop();
        },
        error: (err) => {
          this.ngxUiLoaderService.stop();
          this.toastr.error("Unable to load AP data", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }
}
